"use client";

import * as React from "react";
import DebugOverlay from "@/components/debug/DebugOverlay";

export type DebugMode = "off" | "hover";

type DebugModeContextValue = {
  mode: DebugMode;
  setMode: (mode: DebugMode) => void;
};

const STORAGE_KEY = "pcubed.debugMode";

const DebugModeContext = React.createContext<DebugModeContextValue>({
  mode: "off",
  setMode: () => {},
});

export function DebugModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = React.useState<DebugMode>("off");
  const [hydrated, setHydrated] = React.useState(false);

  React.useEffect(() => {
    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    if (storedValue === "off") {
      setModeState("off");
    }
    if (
      storedValue === "components" ||
      storedValue === "components-actions" ||
      storedValue === "cursor" ||
      storedValue === "cursor-actions" ||
      storedValue === "hover"
    ) {
      setModeState("hover");
    }
    setHydrated(true);
  }, []);

  const setMode = React.useCallback((nextMode: DebugMode) => {
    setModeState(nextMode);
    window.localStorage.setItem(STORAGE_KEY, nextMode);
  }, []);

  const value = React.useMemo<DebugModeContextValue>(
    () => ({ mode, setMode }),
    [mode, setMode],
  );

  return (
    <DebugModeContext.Provider value={value}>
      {children}
      {hydrated ? <DebugOverlay mode={mode} /> : null}
    </DebugModeContext.Provider>
  );
}

export function useDebugMode() {
  return React.useContext(DebugModeContext);
}
